// src/components/defined/activity_overview/ActivityStructureTab.jsx

import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ACTIVITY STRUCTURE TAB — ZONE A1
   ----------------------------------------------------------------------------
   OBJECTIF
   - représenter la structure Activity Corelia
   - afficher une ligne globale projet
   - afficher les phases de niveau 1
   - afficher les sous-activités de niveau 2
   - permettre une édition légère des pourcentages et statuts

   PRINCIPES DE RESTRUCTURATION
   - séparation claire entre :
     1. enveloppe neutre
     2. surface visuelle principale
     3. contenus secondaires (description / sous-activités)
   - conservation stricte des positions internes déjà validées
   - hover appliqué uniquement à la surface principale de carte
   - aucune modification de la ligne projet total
============================================================================ */

/* ============================================================================
   TOKENS UI LOCAUX
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.darkblue || "#b2c1c7",

  coreliaBlue: theme?.colors?.text?.corelia || "#0E3F56",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  blueBorder: theme?.Generic?.blue?.pastel?.border || "#B7C6CC",

  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  greenBorder: theme?.Generic?.green?.pastel?.border || "#B9CEC4",

  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  yellowBorder: theme?.Generic?.yellow?.pastel?.border || "#D8C6A6",

  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  greyBorder: theme?.Generic?.grey?.pastel?.border || "#C7D0D6",

  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",

  purpleText: theme?.Generic?.purple?.pastel?.text || "#4A2E70",

  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
  redBorder: theme?.Generic?.red?.pastel?.border || "#D6B3B3",
};

/* ============================================================================
   CONSTANTES DE GRILLE
   ----------------------------------------------------------------------------
   Centralisées pour éviter les décalages entre :
   - ProjectTotalRow
   - surface principale des GroupCard
   - sous-structures internes
============================================================================ */

const MAIN_GRID_COLUMNS = "25% 45% 25%";
const MAIN_GRID_GAP = 8;

const PRIMARY_BLOCK_COLUMNS = "35.714% 64.286%";

const GROUP_TEXT_COLUMNS = "26px minmax(0, 1fr)";
const ACTIVITY_TEXT_COLUMNS = "48px minmax(0, 1fr)";
const ACTIVITY_GRID_COLUMNS = "48px minmax(220px, 0.7fr) 250px minmax(0, 1fr)";

/* ============================================================================
   PALETTES DES BARRES DELIVERY PAR PHASE
============================================================================ */

const DELIVERY_SEGMENT_STYLES = {
  1: { bg: "#D9E1E6", text: T.greyText, border: "#B7C3CA" },
  2: { bg: "#CFE3EC", text: T.blueText, border: "#97B4C1" },
  3: { bg: "#F0E3BF", text: T.yellowText, border: "#CDAF63" },
  4: { bg: "#CEE6DC", text: T.greenText, border: "#92BEAF" },
  5: { bg: "#EED7C9", text: T.orangeText, border: "#C99B82" },
  6: { bg: "#DED1EE", text: T.purpleText, border: "#B6A1D2" },
};

/* ============================================================================
   PALETTES DE TEXTE DES STATUTS
============================================================================ */

const STATUS_STYLES = {
  Proposed: { text: T.greyText, border: T.greyBorder },
  Validated: { text: T.greenText, border: T.greenBorder },
  Adjusted: { text: T.blueText, border: T.blueBorder },
  Excluded: { text: T.redText, border: T.redBorder },
};

const STATUS_OPTIONS = ["Proposed", "Validated", "Adjusted", "Excluded"];
const DEFAULT_DELIVERY_METHOD = "EPC";
const DEFAULT_TOTAL_DURATION = 24;

/* ============================================================================
   SCHÉMA DELIVERY PAR DÉFAUT
============================================================================ */

const DEFAULT_DELIVERY_SCHEME = [
  { code: "1", label: "Pilotage global EPC", start: 0, end: 100, projectPercent: 20 },
  { code: "2", label: "Ingénierie", start: 8, end: 36, projectPercent: 25 },
  { code: "3", label: "Achats & Approvisionnements", start: 22, end: 54, projectPercent: 15 },
  { code: "4", label: "Construction & Montage", start: 34, end: 88, projectPercent: 25 },
  { code: "5", label: "Mise en service & Performance", start: 82, end: 96, projectPercent: 8 },
  { code: "6", label: "Transfert, Formation & Clôture", start: 92, end: 100, projectPercent: 7 },
];

/* ============================================================================
   DONNÉES PAR DÉFAUT — GROUPES ET ACTIVITÉS
============================================================================ */

const DEFAULT_ROWS = [
  {
    id: "g-1",
    level: "group",
    code: "1",
    title: "Pilotage global EPC",
    description:
      "Coordination générale du contrat clé en main, gestion planning, budget, qualité, sécurité, interfaces, risques et reporting.",
  },
  { id: "g-1-1", parentId: "g-1", level: "activity", code: "1.1", title: "Phase pré-contractuelle et offre", description: "", status: "Validated", phasePercent: 45 },
  { id: "g-1-2", parentId: "g-1", level: "activity", code: "1.2", title: "Cadrage post-award (lancement projet)", description: "", status: "Proposed", phasePercent: 55 },

  { id: "g-2", level: "group", code: "2", title: "Ingénierie", description: "" },
  { id: "g-2-1", parentId: "g-2", level: "activity", code: "2.1", title: "Réaliser les études préliminaires", description: "Analyse besoins, compléments de données, études préalables, variantes.", status: "Validated", phasePercent: 15 },
  { id: "g-2-2", parentId: "g-2", level: "activity", code: "2.2", title: "Réaliser les études de conception", description: "APS, APD, dimensionnements, notes de calcul, plans et schémas.", status: "Validated", phasePercent: 30 },
  { id: "g-2-3", parentId: "g-2", level: "activity", code: "2.3", title: "Réaliser les études d’exécution", description: "Plans EXE détaillés, plans de fabrication, isométriques, dossiers techniques.", status: "Proposed", phasePercent: 28 },
  { id: "g-2-4", parentId: "g-2", level: "activity", code: "2.4", title: "Coordonner et valider les études", description: "Revue qualité, coordination disciplines, conformité exigences et normes.", status: "Adjusted", phasePercent: 17 },
  { id: "g-2-5", parentId: "g-2", level: "activity", code: "2.5", title: "Gérer les modifications techniques", description: "Gestion des change requests, analyse impacts coûts/délais, mise à jour dossiers.", status: "Proposed", phasePercent: 10 },

  { id: "g-3", level: "group", code: "3", title: "Achats & Approvisionnements", description: "" },
  { id: "g-3-1", parentId: "g-3", level: "activity", code: "3.1", title: "Préparer la stratégie achats", description: "Sourcing, segmentation, stratégie make-or-buy, planning achats.", status: "Validated", phasePercent: 14 },
  { id: "g-3-2", parentId: "g-3", level: "activity", code: "3.2", title: "Lancer les consultations", description: "Spécifications, DCE fournisseurs, appels d’offres, clarifications.", status: "Validated", phasePercent: 16 },
  { id: "g-3-3", parentId: "g-3", level: "activity", code: "3.3", title: "Analyser et sélectionner les offres", description: "Comparatifs techniques/financiers, négociations, recommandations.", status: "Adjusted", phasePercent: 18 },
  { id: "g-3-4", parentId: "g-3", level: "activity", code: "3.4", title: "Contractualiser et passer commandes", description: "Contrats fournisseurs, commandes, clauses performance, délais.", status: "Validated", phasePercent: 18 },
  { id: "g-3-5", parentId: "g-3", level: "activity", code: "3.5", title: "Suivre la fabrication et la qualité", description: "Inspections usine, FAT, contrôles conformité, traçabilité production.", status: "Proposed", phasePercent: 17 },
  { id: "g-3-6", parentId: "g-3", level: "activity", code: "3.6", title: "Gérer le transport et la logistique", description: "Transport maritime/route, douanes, stockage, manutention, livraisons.", status: "Proposed", phasePercent: 17 },

  { id: "g-4", level: "group", code: "4", title: "Construction & Montage", description: "" },
  { id: "g-4-1", parentId: "g-4", level: "activity", code: "4.1", title: "Préparer le chantier", description: "Base-vie, installations chantier, accès, stockage, plan HSE chantier.", status: "Validated", phasePercent: 10 },
  { id: "g-4-2", parentId: "g-4", level: "activity", code: "4.2", title: "Réaliser les travaux civils", description: "Terrassements, fondations, structures, dallages, réservoirs, réseaux enterrés.", status: "Validated", phasePercent: 26 },
  { id: "g-4-3", parentId: "g-4", level: "activity", code: "4.3", title: "Installer équipements et tuyauteries", description: "Installation machines, équipements électromécaniques, tuyauteries, supports.", status: "Validated", phasePercent: 22 },
  { id: "g-4-4", parentId: "g-4", level: "activity", code: "4.4", title: "Réaliser installations électriques et contrôle-commande", description: "Câblage puissance/commande, armoires, instrumentation, bouclages.", status: "Proposed", phasePercent: 18 },
  { id: "g-4-5", parentId: "g-4", level: "activity", code: "4.5", title: "Réaliser tests de montage", description: "Vérifications mécaniques/électriques, tests circuits, flushing, calibrations.", status: "Adjusted", phasePercent: 12 },
  { id: "g-4-6", parentId: "g-4", level: "activity", code: "4.6", title: "Gérer HSE et qualité chantier", description: "Suivi HSE, contrôles qualité, audits, non-conformités, actions correctives.", status: "Proposed", phasePercent: 12 },

  { id: "g-5", level: "group", code: "5", title: "Mise en service & Performance", description: "" },
  { id: "g-5-1", parentId: "g-5", level: "activity", code: "5.1", title: "Préparer la mise en service", description: "Plans de tests, procédures d’essais, prérequis, check-lists.", status: "Validated", phasePercent: 22 },
  { id: "g-5-2", parentId: "g-5", level: "activity", code: "5.2", title: "Réaliser la mise en route", description: "Essais à vide, essais partiels, réglages, synchronisation systèmes.", status: "Validated", phasePercent: 28 },
  { id: "g-5-3", parentId: "g-5", level: "activity", code: "5.3", title: "Vérifier les performances contractuelles", description: "Tests de performance, mesures, conformité exigences garanties.", status: "Proposed", phasePercent: 30 },
  { id: "g-5-4", parentId: "g-5", level: "activity", code: "5.4", title: "Formaliser la réception provisoire", description: "Procès-verbaux, réserves, plan de levée, transfert vers exploitation.", status: "Proposed", phasePercent: 20 },

  { id: "g-6", level: "group", code: "6", title: "Transfert, Formation & Clôture", description: "" },
  { id: "g-6-1", parentId: "g-6", level: "activity", code: "6.1", title: "Former le personnel client", description: "Formations exploitation, maintenance, sécurité, procédures.", status: "Proposed", phasePercent: 22 },
  { id: "g-6-2", parentId: "g-6", level: "activity", code: "6.2", title: "Transférer la documentation et les DOE", description: "DOE complet, manuels, plans finaux, certificats de conformité.", status: "Validated", phasePercent: 28 },
  { id: "g-6-3", parentId: "g-6", level: "activity", code: "6.3", title: "Assurer la phase de garantie et support", description: "Support post-démarrage, assistance exploitation, traitement réclamations.", status: "Proposed", phasePercent: 24 },
  { id: "g-6-4", parentId: "g-6", level: "activity", code: "6.4", title: "Clôturer le contrat et réaliser la réception définitive", description: "Levée finale réserves, bilan projet, clôture contractuelle et administrative.", status: "Proposed", phasePercent: 26 },
];

/* ============================================================================
   UTILITAIRES
============================================================================ */

/**
 * Formate systématiquement une valeur numérique avec 1 décimale.
 */
const formatOneDecimal = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "0.0";
  return num.toFixed(1);
};

/**
 * Nettoie une saisie de pourcentage :
 * - conserve vide si l'utilisateur efface
 * - borne entre 0 et 100
 */
const parsePercentInput = (raw) => {
  if (raw === "") return "";
  const num = Number(raw);
  if (Number.isNaN(num)) return 0;
  return Math.max(0, Math.min(100, num));
};

/**
 * Convertit un pourcentage projet en nombre de semaines
 * au prorata de la durée totale du projet.
 */
const getWeeksFromProjectPercent = (projectPercent, totalDuration) => {
  const percent = Number(projectPercent);
  const duration = Number(totalDuration);

  if (Number.isNaN(percent) || Number.isNaN(duration)) return 0;

  const totalWeeks = duration * (52 / 12);
  return Math.round((totalWeeks * percent) / 100);
};

/**
 * Convertit un pourcentage de sous-activité en semaines,
 * au prorata de la phase parente déjà exprimée en semaines.
 */
const getWeeksFromParentPhase = (activityPercent, parentPhaseWeeks) => {
  const percent = Number(activityPercent);
  const weeks = Number(parentPhaseWeeks);

  if (Number.isNaN(percent) || Number.isNaN(weeks)) return 0;

  return Math.round((weeks * percent) / 100);
};

/* ============================================================================
   SOUS-COMPOSANTS UI COMMUNS
============================================================================ */

const PercentControl = ({
  value,
  onChange,
  disabled = false,
  weeksLabel = null,
  labelMinWidth = null,
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.70rem",
          lineHeight: 1,
          color: T.soft,
          fontWeight: 600,
        }}
      >
        %
      </div>

      <PercentInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        showSymbolInside={false}
      />
    </div>

    {weeksLabel !== null && (
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.70rem",
          lineHeight: 1.1,
          color: T.verySoft,
          fontWeight: 500,
          whiteSpace: "nowrap",
          minWidth: labelMinWidth || "auto",
        }}
      >
        [{weeksLabel} semaines]
      </div>
    )}
  </div>
);

/* ============================================================================
   COMPOSANT — CHAMP POURCENTAGE
============================================================================ */

const PercentInput = ({
  value,
  onChange,
  disabled = false,
  showSymbolInside = false,
}) => (
  <div
    style={{
      width: showSymbolInside ? 87 : 69,
      height: 26,
      borderRadius: 3,
      border: `1px solid ${T.border}`,
      background: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      opacity: disabled ? 0.7 : 1,
    }}
  >
    <input
      type="number"
      min={0}
      max={100}
      step={1}
      value={value === "" ? "" : formatOneDecimal(value)}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: showSymbolInside ? 54 : 48,
        border: "none",
        background: "transparent",
        outline: "none",
        textAlign: "right",
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.70rem",
        lineHeight: 1,
        fontWeight: 600,
        color: T.text,
        padding: 0,
        margin: 0,
      }}
    />
    {showSymbolInside && (
      <span
        style={{
          fontSize: "0.68rem",
          color: T.soft,
          fontWeight: 600,
          marginLeft: 1,
        }}
      >
        %
      </span>
    )}
  </div>
);

/* ============================================================================
   COMPOSANT — SÉLECTEUR DE STATUT
============================================================================ */

const StatusSelect = ({ value, onChange }) => {
  const style = STATUS_STYLES[value] || STATUS_STYLES.Proposed;

  return (
    <div
      style={{
        position: "relative",
        width: 126,
        height: 26,
        borderRadius: 3,
        border: `1px solid ${T.border}`,
        background: "#ffffff",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          background: "transparent",
          color: style.text,
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.68rem",
          fontWeight: 600,
          padding: "0 22px 0 8px",
          outline: "none",
          cursor: "pointer",
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
        }}
      >
        {STATUS_OPTIONS.map((option) => {
          const optionStyle = STATUS_STYLES[option];
          return (
            <option key={option} value={option} style={{ color: optionStyle.text }}>
              {option}
            </option>
          );
        })}
      </select>

      <div
        style={{
          position: "absolute",
          right: 7,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          fontSize: "0.68rem",
          color: style.text,
          fontWeight: 700,
        }}
      >
        ▾
      </div>
    </div>
  );
};

/* ============================================================================
   COMPOSANT — BARRE DELIVERY DE PHASE
============================================================================ */

const DeliveryBand = ({ segment, selected = false }) => {
  const style = DELIVERY_SEGMENT_STYLES[segment.code] || DELIVERY_SEGMENT_STYLES[1];
  const start = Math.max(0, Math.min(100, segment.start));
  const end = Math.max(start, Math.min(100, segment.end));
  const width = Math.max(6, end - start);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: 12,
        borderRadius: 999,
        background: "transparent",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: `${start}%`,
          width: `${width}%`,
          top: 0,
          bottom: 0,
          borderRadius: 999,
          background: style.bg,
          border: `1px solid ${style.border}`,
          boxSizing: "border-box",
          boxShadow: selected ? theme?.shadows?.xs : "none",
        }}
      />
    </div>
  );
};

/* ============================================================================
   COMPOSANT — BARRE PROJET TOTAL
============================================================================ */

const ProjectTotalBand = () => (
  <div
    style={{
      position: "relative",
      width: "100%",
      height: 12,
      borderRadius: 999,
      background: "transparent",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        position: "absolute",
        left: 0,
        width: "100%",
        top: 0,
        bottom: 0,
        borderRadius: 999,
        background: T.coreliaBlue,
        border: `1px solid ${T.coreliaBlue}`,
        boxSizing: "border-box",
      }}
    />
  </div>
);

/* ============================================================================
   COMPOSANT — ENVELOPPE NEUTRE
============================================================================ */

const BarsLineShell = ({ children }) => {
  return (
    <div
      style={{
        width: "100%",
        background: "transparent",
        border: "none",
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );
};

/* ============================================================================
   COMPOSANT — SURFACE PRINCIPALE DE LIGNE
   ----------------------------------------------------------------------------
   Surface mutualisée pour :
   - ProjectTotalRow
   - ligne principale des phases
============================================================================ */

const MainLineGrid = ({ children, hover = false, onMouseEnter, onMouseLeave }) => (
  <div
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      display: "grid",
      width: "95%",
      gridTemplateColumns: MAIN_GRID_COLUMNS,
      gap: MAIN_GRID_GAP,
      alignItems: "center",
      borderRadius: 6,
      boxSizing: "border-box",
      border: hover ? `1px solid ${T.borderStrong}` : "1px solid transparent",
      transition: "border 0.12s ease-in-out",
    }}
  >
    {children}
  </div>
);

/* ============================================================================
   COMPOSANT — LIGNE D’ACTIVITÉ NIVEAU 2
============================================================================ */

const ActivityLine = ({
  item,
  selected = false,
  status = "Proposed",
  phasePercent = 0,
  parentPhaseWeeks = 0,
  onClick,
  onStatusChange,
  onPercentChange,
}) => {
  const hasDescription = !!item.description;
  const isExcluded = status === "Excluded";
  const activityWeeks = getWeeksFromParentPhase(
    phasePercent === "" ? 0 : phasePercent,
    parentPhaseWeeks
  );

  return (
    <div
      style={{
        width: "100%",
        background: "transparent",
        borderRadius: 6,
        opacity: isExcluded ? 0.72 : 1,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: ACTIVITY_GRID_COLUMNS,
          gap: 8,
          alignItems: selected && hasDescription ? "start" : "center",
          padding: "7px 0",
        }}
      >
        {/* BLOC TEXTE DE L’ACTIVITÉ */}
        <button
          type="button"
          onClick={onClick}
          style={{
            gridColumn: "1 / 3",
            display: "grid",
            gridTemplateColumns: ACTIVITY_TEXT_COLUMNS,
            gap: 8,
            alignItems: selected && hasDescription ? "start" : "center",
            width: "100%",
            border: "none",
            background: "transparent",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            textAlign: "left",
            minHeight: selected && hasDescription ? "auto" : 26,
          }}
        >
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.74rem",
              lineHeight: 1.15,
              color: selected ? T.text : T.soft,
              fontWeight: selected ? 600 : 500,
              alignSelf: selected && hasDescription ? "start" : "center",
            }}
          >
            {item.code}
          </div>

          <div
            style={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: selected && hasDescription ? "flex-start" : "center",
              gap: selected && hasDescription ? 5 : 0,
              minHeight: selected && hasDescription ? "auto" : 26,
            }}
          >
            <div
              style={{
                fontFamily: theme?.fonts?.body || "inherit",
                fontSize: "0.75rem",
                lineHeight: 1.18,
                color: T.text,
                fontWeight: selected ? 600 : 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.title}
            </div>

            {selected && hasDescription && (
              <div
                style={{
                  fontFamily: theme?.fonts?.body || "inherit",
                  fontSize: "0.68rem",
                  lineHeight: 1.32,
                  color: T.verySoft,
                  maxWidth: "88%",
                }}
              >
                {item.description}
              </div>
            )}
          </div>
        </button>

        {/* BLOC CONTRÔLES */}
        <div
          style={{
            gridColumn: "3 / 4",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 12,
            minHeight: 26,
            width: "100%",
          }}
        >
          <PercentControl
            value={phasePercent}
            onChange={onPercentChange}
            disabled={isExcluded}
            weeksLabel={activityWeeks}
            labelMinWidth={74}
          />

          <StatusSelect value={status} onChange={onStatusChange} />
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   COMPOSANT — LIGNE PRINCIPALE DE PHASE
   ----------------------------------------------------------------------------
   Surface visuelle de la carte :
   - porte le hover
   - ne contient ni description ni sous-activités
============================================================================ */

const GroupMainRow = ({
  group,
  segment,
  phasePercentValue,
  weeksLabel,
  isActiveGroup,
  isExpanded,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onToggleGroup,
  onPhasePercentChange,
}) => {
  return (
    <MainLineGrid
      hover={isHovered}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* BLOC PRINCIPAL : code/titre + barre */}
      <button
        type="button"
        onClick={onToggleGroup}
        style={{
          gridColumn: "1 / 3",
          display: "grid",
          gridTemplateColumns: PRIMARY_BLOCK_COLUMNS,
          alignItems: "center",
          width: "100%",
          border: "none",
          background: "transparent",
          padding: 0,
          margin: 0,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {/* CODE + TITRE */}
        <div
          style={{
            minWidth: 0,
            display: "grid",
            gridTemplateColumns: GROUP_TEXT_COLUMNS,
            gap: 6,
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.78rem",
              lineHeight: 1.1,
              color: T.text,
              fontWeight: 700,
            }}
          >
            {group.code}
          </div>

          <div
            style={{
              minWidth: 0,
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.80rem",
              lineHeight: 1.16,
              color: T.text,
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {group.title}
          </div>
        </div>

        {/* BARRE PHASE */}
        <div style={{ width: "100%" }}>
          {segment && (
            <DeliveryBand
              segment={segment}
              selected={isActiveGroup || isExpanded}
            />
          )}
        </div>
      </button>

                  {/* % PHASE */}
      <div style={{ display: "flex", justifyContent: "flex-start", paddingLeft: "8%" }}>
        <PercentControl
          value={phasePercentValue}
          onChange={onPhasePercentChange}
          weeksLabel={weeksLabel}
        />
      </div>

      {/* COLONNE FINALE VIDE */}
      <div />
    </MainLineGrid>
  );
};

/* ============================================================================
   COMPOSANT — CARTE DE PHASE NIVEAU 1
   ----------------------------------------------------------------------------
   Structure propre :
   1. enveloppe neutre
   2. ligne principale hoverable
   3. description hors hover
   4. sous-activités hors hover
============================================================================ */

const GroupCard = ({
  group,
  childrenRows = [],
  segment,
  phasePercentValue,
  totalDuration,
  isExpanded = false,
  isActiveGroup = false,
  selectedId,
  activityMeta,
  onToggleGroup,
  onSelectItem,
  onActivityMetaChange,
  onPhasePercentChange,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const isGroupDescriptionVisible = selectedId === group.id && !!group.description;

  const weeksLabel = getWeeksFromProjectPercent(
    phasePercentValue === "" ? 0 : phasePercentValue,
    totalDuration
  );

  const parentPhaseWeeks = getWeeksFromProjectPercent(
    phasePercentValue === "" ? 0 : phasePercentValue,
    totalDuration
  );

  return (
    <BarsLineShell>
      {/* ENVELOPPE NEUTRE GLOBALE */}
      <div
        style={{
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* SURFACE PRINCIPALE DE CARTE */}
        <GroupMainRow
          group={group}
          segment={segment}
          phasePercentValue={phasePercentValue}
          weeksLabel={weeksLabel}
          isActiveGroup={isActiveGroup}
          isExpanded={isExpanded}
          isHovered={isHovered}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onToggleGroup={onToggleGroup}
          onPhasePercentChange={onPhasePercentChange}
        />

        {/* DESCRIPTION DE PHASE */}
        {isGroupDescriptionVisible && (
          <div
            style={{
              marginTop: 8,
              marginLeft: 32,
              maxWidth: "60%",
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.69rem",
              lineHeight: 1.34,
              color: T.verySoft,
            }}
          >
            {group.description}
          </div>
        )}

        {/* SOUS-ACTIVITÉS */}
        {isExpanded && childrenRows.length > 0 && (
          <div
            style={{
              marginTop: 10,
              marginLeft: 20,
              paddingLeft: 14,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {childrenRows.map((item) => {
              const meta = activityMeta[item.id] || {
                status: item.status || "Proposed",
                phasePercent: item.phasePercent || 0,
              };

              return (
                <ActivityLine
                  key={item.id}
                  item={item}
                  selected={selectedId === item.id}
                  status={meta.status}
                  phasePercent={meta.phasePercent}
                  parentPhaseWeeks={parentPhaseWeeks}
                  onClick={() => onSelectItem(item)}
                  onStatusChange={(nextStatus) =>
                    onActivityMetaChange(item.id, { status: nextStatus })
                  }
                  onPercentChange={(nextPercent) =>
                    onActivityMetaChange(item.id, { phasePercent: nextPercent })
                  }
                />
              );
            })}
          </div>
        )}
      </div>
    </BarsLineShell>
  );
};

/* ============================================================================
   COMPOSANT — LIGNE GLOBALE PROJET
============================================================================ */

const ProjectTotalRow = ({ totalDuration }) => (
  <BarsLineShell>
    <MainLineGrid hover={false}>
      {/* BLOC PRINCIPAL : libellé + barre */}
      <div
        style={{
          gridColumn: "1 / 3",
          display: "grid",
          gridTemplateColumns: PRIMARY_BLOCK_COLUMNS,
          alignItems: "center",
        }}
      >
        <div
          style={{
            minWidth: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.80rem",
              lineHeight: 1.16,
              color: T.text,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Durée totale projet : {totalDuration} mois
          </div>
        </div>

        <div style={{ width: "100%" }}>
          <ProjectTotalBand />
        </div>
      </div>

                  {/* % PROJET TOTAL */}
      <div style={{ display: "flex", justifyContent: "flex-start", paddingLeft: "8%" }}>
        <PercentControl
          value={100}
          onChange={() => {}}
          disabled={true}
          weeksLabel={null}
        />
      </div>

      {/* COLONNE FINALE VIDE */}
      <div />
    </MainLineGrid>
  </BarsLineShell>
);

/* ============================================================================
   COMPOSANT PRINCIPAL
============================================================================ */

const ActivityStructureTab = ({
  deliveryMethod = DEFAULT_DELIVERY_METHOD,
  totalDuration = DEFAULT_TOTAL_DURATION,
  deliveryScheme = DEFAULT_DELIVERY_SCHEME,
  rows = DEFAULT_ROWS,
  onSelectChain,
}) => {
  /* ------------------------------------------------------------------------
     Liste des phases niveau 1
  ------------------------------------------------------------------------ */
  const groupRows = useMemo(() => rows.filter((item) => item.level === "group"), [rows]);

  /* ------------------------------------------------------------------------
     Indexation des activités par parent
  ------------------------------------------------------------------------ */
  const childrenByParent = useMemo(() => {
    const map = {};
    rows.forEach((item) => {
      if (item.parentId) {
        if (!map[item.parentId]) map[item.parentId] = [];
        map[item.parentId].push(item);
      }
    });
    return map;
  }, [rows]);

  /* ------------------------------------------------------------------------
     État initial des métadonnées activités
  ------------------------------------------------------------------------ */
  const initialActivityMeta = useMemo(() => {
    const map = {};
    rows
      .filter((item) => item.level === "activity")
      .forEach((item) => {
        map[item.id] = {
          status: item.status || "Proposed",
          phasePercent:
            typeof item.phasePercent === "number" ? Number(item.phasePercent.toFixed(1)) : 0,
        };
      });
    return map;
  }, [rows]);

  /* ------------------------------------------------------------------------
     État initial des pourcentages de phase
  ------------------------------------------------------------------------ */
  const initialPhaseMeta = useMemo(() => {
    const map = {};
    deliveryScheme.forEach((segment) => {
      const base =
        typeof segment.projectPercent === "number"
          ? segment.projectPercent
          : Math.max(0, Math.round(segment.end - segment.start));

      map[segment.code] = Number(base.toFixed(1));
    });
    return map;
  }, [deliveryScheme]);

  /* ------------------------------------------------------------------------
     États locaux
  ------------------------------------------------------------------------ */
  const [expandedGroupId, setExpandedGroupId] = useState("g-4");
  const [selectedId, setSelectedId] = useState("g-4");
  const [activityMeta, setActivityMeta] = useState(initialActivityMeta);
  const [phaseMeta, setPhaseMeta] = useState(initialPhaseMeta);

  /* ------------------------------------------------------------------------
     Élément sélectionné
  ------------------------------------------------------------------------ */
  const selectedItem = useMemo(
    () => rows.find((item) => item.id === selectedId) || null,
    [rows, selectedId]
  );

  /* ------------------------------------------------------------------------
     Groupe actif
  ------------------------------------------------------------------------ */
  const activeGroupId = useMemo(() => {
    if (!selectedItem) return expandedGroupId;
    return selectedItem.level === "group" ? selectedItem.id : selectedItem.parentId;
  }, [selectedItem, expandedGroupId]);

  /* ------------------------------------------------------------------------
     Ouvrir / fermer une phase
  ------------------------------------------------------------------------ */
  const handleToggleGroup = (group) => {
    setExpandedGroupId((prev) => (prev === group.id ? null : group.id));
    setSelectedId(group.id);
    onSelectChain?.(group);
  };

  /* ------------------------------------------------------------------------
     Sélectionner une activité
  ------------------------------------------------------------------------ */
  const handleSelectItem = (item) => {
    setSelectedId(item.id);

    if (item.parentId && expandedGroupId !== item.parentId) {
      setExpandedGroupId(item.parentId);
    }

    onSelectChain?.(item);
  };

  /* ------------------------------------------------------------------------
     Mise à jour d'une sous-activité
  ------------------------------------------------------------------------ */
  const handleActivityMetaChange = (itemId, patch) => {
    setActivityMeta((prev) => {
      const current = prev[itemId] || { status: "Proposed", phasePercent: 0 };
      const raw = patch.phasePercent !== undefined ? patch.phasePercent : current.phasePercent;
      const nextPercent = raw === "" ? "" : Number(parsePercentInput(raw).toFixed(1));

      return {
        ...prev,
        [itemId]: {
          ...current,
          ...patch,
          phasePercent:
            patch.phasePercent !== undefined ? nextPercent : current.phasePercent,
        },
      };
    });
  };

  /* ------------------------------------------------------------------------
     Mise à jour d'un % phase niveau 1
  ------------------------------------------------------------------------ */
  const handlePhasePercentChange = (code, nextValue) => {
    setPhaseMeta((prev) => ({
      ...prev,
      [code]:
        nextValue === ""
          ? ""
          : Number(parsePercentInput(nextValue).toFixed(1)),
    }));
  };

  /* ------------------------------------------------------------------------
     RENDER
  ------------------------------------------------------------------------ */
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        boxSizing: "border-box",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          paddingBottom: 2,
        }}
      >
        <div
          style={{
            fontFamily: theme?.fonts?.body || "inherit",
            fontSize: "0.72rem",
            lineHeight: 1.1,
            color: T.verySoft,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Activity structure
        </div>

        <div
          style={{
            fontFamily: theme?.fonts?.body || "inherit",
            fontSize: "1.03rem",
            lineHeight: 1.12,
            color: T.text,
            fontWeight: 600,
          }}
        >
          Delivery scheme proposed by Corelia
        </div>

        <div
          style={{
            fontFamily: theme?.fonts?.body || "inherit",
            fontSize: "0.73rem",
            lineHeight: 1.35,
            color: T.verySoft,
          }}
        >
          {deliveryMethod} · {totalDuration} months
        </div>
      </div>

      {/* CONTENU */}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {/* LIGNE GLOBALE PROJET */}
        <ProjectTotalRow totalDuration={totalDuration} />

        {/* ESPACE ENTRE PROJET TOTAL ET PHASES */}
        <div style={{ height: 10 }} />

        {/* PHASES NIVEAU 1 */}
        {groupRows.map((group) => {
          const segment = deliveryScheme.find((item) => item.code === group.code) || null;

          return (
            <GroupCard
              key={group.id}
              group={group}
              segment={segment}
              phasePercentValue={phaseMeta[group.code] ?? ""}
              totalDuration={totalDuration}
              childrenRows={childrenByParent[group.id] || []}
              isExpanded={expandedGroupId === group.id}
              isActiveGroup={activeGroupId === group.id}
              selectedId={selectedId}
              activityMeta={activityMeta}
              onToggleGroup={() => handleToggleGroup(group)}
              onSelectItem={handleSelectItem}
              onActivityMetaChange={handleActivityMetaChange}
              onPhasePercentChange={(nextValue) =>
                handlePhasePercentChange(group.code, nextValue)
              }
            />
          );
        })}
      </div>
    </div>
  );
};

export default ActivityStructureTab;